// This file contains the notification maps moved from the cart project
// to break circular dependencies between cart and common-next

import { decodeEntityString } from '@libs/algolia';
import { Domains, getLocale, Locale } from '@libs/core';

// Define the error keys as enums
export enum CartValidationErrorKey {
    PLACEHOLDER = 'placeholder.todo',
    UNEXPECTED_ERROR_BANNER = 'cart.checkout.unexpectederrorbanner',
    MIXED_CART_BANNER = 'cart.samedaydelivery.mixedcartbanner',
    UPDATE_FAILURE_BANNER = 'cart.checkout.updatefailurebanner',
    OUT_OF_STOCK = 'cart.checkout.outofstock',
    INSUFFICIENT_STOCK = 'cart.checkout.insufficientstock',
    PICKUP_STORE_CLOSED = 'cartcheckout.bopisdisabled',
    MIXED_RX_COLD_ITEM = 'cart.vetrx.colditembanner',
    MISSING_CUSTOMER_EMAIL = 'cart.missingemail',
    LOYALTY_UNAVAILABLE = 'cart.loyalty.unavailable',
    AUTOSHIP_START_DATE_CHANGED = 'cart.autoship.startdatechanged',
    AUTOSHIP_FUTURE_START_DATE_ERROR = 'cart.autoship.futurestartdateerror',
    AUTOSHIP_SCHEDULED_DATE_INVALID_FULFILLMENT_METHOD = 'cart.checkout.scheduledDeliveryDate.invalidFulfillmentMethod',
    AUTOSHIP_SCHEDULED_DATE_OUT_OF_ALLOWED_RANGE = 'cart.checkout.scheduledDeliveryDate.outOfAllowedRange',
    DISCONTINUED_PRODUCT = 'cart.discontinuedproduct',
    SDD_WEIGHT_LIMIT = 'cart.sdd.overweight',
    INELIGIBLE_ITEM = 'cart.checkout.ineligibleitem',
    CHECKOUT_EMPTY = 'cart.checkout.empty',
    AUTOSHIP_NOT_SUPPORTED = 'cart.checkout.autoship.notSupported',
    FULFILLMENT_METHOD_CANNOT_BE_CHANGED = 'cart.fulfillmentMethod.cannotBeChanged',
    TREAT_POINTS_REMOVED = 'cart.treatpointsremoved.insufficientbalance',
    LOYALTY_DOWN = 'cart.loyaltydown',
}

export enum CheckoutValidationErrorKey {
    ADD_CARD_FAILURE_BANNER = 'checkout.addcardfailurebanner',
    PAYMENT_FAILURE = 'checkout.ordersubmit.paymentauthfailure',
    MARKET_SHIPPING_ADDRESS_ERROR = 'checkout.marketshippingaddresserror',
    PAYMENT_SERVICE_ERROR = 'checkout.paymentserviceerror',
    SDD_WEIGHT_LIMIT = 'sdd-weight-limit',
    MISSING_SHIPPING_DETAILS = 'checkout.lineitem.missingshippingdetails',
    MISSING_CREDIT_CARD_FOR_AUTOSHIP = 'checkout.nocardsforautoship',
    SDD_ADDRESS_UNAVAILABLE = 'checkout.SDDaddressunavailable',
    RX_MED_RESTRICTED_TERRITORY = 'checkout.shippingaddress.rxmedrestrictedterritory',
    DROPSHIP_RESTRICTED_TERRITORY = 'checkout.shippingaddress.dropshiprestrictedterritory',
    SDD_INVALID_DELIVERY_WINDOW = 'checkout.SddInvalidDeliveryWindow',
    AMOUNT_MISMATCH = 'checkout.payments.amountmismatch',
}

// Define the notification types
export type NotificationId =
    | 'quantity-adjusted'
    | 'autoship-subscription'
    | 'autoship-saving'
    | 'multiple-pharmacy-items'
    | 'multi-store-bopis'
    | 'merged-cart'
    | 'autoship-override-cart-updated-persisted'
    | 'max-quantity-exceeded'
    | 'autoship-item-removed'
    | 'scheduled-delivery-date-updated'
    | 'start-date-not-changed'
    | 'payment-method-unavailable'
    | CartValidationErrorKey
    | CheckoutValidationErrorKey;

export type Notification = {
    id: NotificationId;
    variant: 'info' | 'warning' | 'success' | 'error';
    header: string;
    text: string;
    list?: string[] | Array<{ text: string; url: string }>;
    closable?: boolean;
};

// Utility functions to create notification objects
export const createGenericErrorNotification = (): Notification => ({
    id: CartValidationErrorKey.PLACEHOLDER,
    variant: 'error',
    header: 'Something went wrong',
    text: 'Please refresh the page and try again',
});

export const createUnexpectedErrorNotification = (): Notification => ({
    id: CartValidationErrorKey.UNEXPECTED_ERROR_BANNER,
    variant: 'error',
    header: 'An unexpected error occurred.',
    text: 'Please try again later.',
});

export const createMixedCartBannerErrorNotification = (): Notification => ({
    id: CartValidationErrorKey.MIXED_CART_BANNER,
    variant: 'error',
    header: 'Delivery error',
    text: 'Please update all items to same-day delivery or remove items from cart to proceed to checkout',
});

export const createAutoshipStartDateChangedBannerNotification = (): Notification => ({
    id: CartValidationErrorKey.AUTOSHIP_START_DATE_CHANGED,
    variant: 'info',
    header: 'Autoship date has changed',
    text: 'Your previously selected date is no longer available and is now set to start today. You can change your date below.',
    closable: true,
});

export const createFutureStartDateErrorBannerNotification = (): Notification => ({
    id: CartValidationErrorKey.AUTOSHIP_FUTURE_START_DATE_ERROR,
    variant: 'error',
    header: 'Future start date error',
    text: 'All items in cart must have the same start date. Please update all items to Autoship or remove items from cart to proceed to checkout.',
    closable: true,
});

export const createMixedRxWithColdItemErrorNotification = (): Notification => ({
    id: CartValidationErrorKey.MIXED_RX_COLD_ITEM,
    variant: 'error',
    header: 'Cold prescription item(s) cannot be shipped with other items',
    text: 'Please purchase cold prescription items separately',
});

export const createIneligibleItemErrorNotification = (): Notification => ({
    id: CartValidationErrorKey.INELIGIBLE_ITEM,
    variant: 'error',
    header: 'Ineligible item',
    text: 'Item(s) in your cart no longer eligible for Autoship with Pick up in store or Same-day delivery. Update your cart to proceed to checkout',
});

export const createMissingShippingDetailsBannerErrorNotification = (): Notification => ({
    id: CheckoutValidationErrorKey.MISSING_SHIPPING_DETAILS,
    variant: 'error',
    header: 'Issue saving shipping address.',
    text: 'Please confirm your shipping address to proceed with checkout',
});

export const createMissingCreditCardForAutoshipBannerErrorNotification = (): Notification => ({
    id: CheckoutValidationErrorKey.MISSING_CREDIT_CARD_FOR_AUTOSHIP,
    variant: 'error',
    header: 'Payment declined',
    text: 'We are unable to process your payment. Please re-enter your payment details or select a different payment method.',
});

export const createPickupStoreErrorNotification = (): Notification => ({
    id: CartValidationErrorKey.PICKUP_STORE_CLOSED,
    variant: 'error',
    header: 'Pickup store temporarily closed,',
    text: 'for one or more items in your cart. Please update selected store',
});

export const createPaymentUnavailableErrorNotification = (): Notification => ({
    id: CheckoutValidationErrorKey.PAYMENT_FAILURE,
    variant: 'error',
    header: 'Payment unavailable',
    text: 'One or more payment methods are not available at this time. Please select a different method to continue',
});

export const createAmountMismatchErrorNotification = (): Notification => ({
    id: CheckoutValidationErrorKey.AMOUNT_MISMATCH,
    variant: 'error',
    header: 'Payment authorization failure',
    text: 'Your cart total has changed. Please try again.',
    closable: true,
});

export const createSddAdrressUnavailableErrorNotification = (): Notification => ({
    id: CheckoutValidationErrorKey.SDD_ADDRESS_UNAVAILABLE,
    variant: 'error',
    header: 'Delivery address not available',
    text: 'Please select another address',
});
export const createSddExpiredDeliveryWindowErrorNotification = (): Notification => ({
    id: CheckoutValidationErrorKey.SDD_INVALID_DELIVERY_WINDOW,
    variant: 'error',
    header: 'Delivery window is no longer available',
    text: 'Please choose another option',
});

export const createDiscontinuedProductErrorNotification = (productNames: string[]): Notification => {
    const deduped = [...new Set(productNames.map(n => decodeEntityString(n.trim())).filter(Boolean))];
    return {
        id: CartValidationErrorKey.DISCONTINUED_PRODUCT,
        variant: 'error',
        header: 'Item(s) removed from cart',
        text: 'The following item(s) are no longer available.',
        ...(deduped.length ? { list: deduped } : {}),
        closable: true,
    };
};

export const createTreatPointsRemovedNotification = (): Notification => ({
    id: CartValidationErrorKey.TREAT_POINTS_REMOVED,
    variant: 'info',
    header: 'Points no longer available',
    text: 'Applied Treats Rewards points were already used or expired. See points history for details',
    closable: true,
});

export const createLoyaltyUnavailableNotification = (): Notification => ({
    id: CartValidationErrorKey.LOYALTY_UNAVAILABLE,
    variant: 'info',
    header: 'Treats Rewards currently unavailable',
    text: 'After purchase, you will receive points on this order when service is restored',
    closable: true,
});

export const createLoyaltyUnavailableAuth0Notification = (): Notification => ({
    id: CartValidationErrorKey.LOYALTY_UNAVAILABLE,
    variant: 'error',
    header: 'Treats Rewards account details are temporarily unavailable.',
    text: 'You can still shop, place orders, book services and continue earning points',
    closable: true,
});

export const createMarketShippingAddressErrorNotification = (): Notification => {
    const domainMapping = {
        [Locale['en-US']]: {
            countryName: 'US',
            targetDomain: Domains['en-CA'].replace('https://www.', ''),
        },
        [Locale['en-CA']]: {
            countryName: 'Canada',
            targetDomain: Domains['en-US'].replace('https://www.', ''),
        },
    };

    const { targetDomain, countryName } = domainMapping[getLocale()];

    return {
        id: CheckoutValidationErrorKey.MARKET_SHIPPING_ADDRESS_ERROR,
        variant: 'error',
        header: 'Invalid address',
        text: `Please navigate to ${targetDomain} or update your shipping address to a ${countryName} address`,
    };
};

const futureStartDateErrorBannerNotification = createFutureStartDateErrorBannerNotification();

// Define the notification maps using the utility functions
export const cartValidationNotificationMap: Partial<Record<CartValidationErrorKey, Notification>> = {
    [CartValidationErrorKey.PLACEHOLDER]: createGenericErrorNotification(),
    [CartValidationErrorKey.UNEXPECTED_ERROR_BANNER]: {
        ...createGenericErrorNotification(),
        id: CartValidationErrorKey.UNEXPECTED_ERROR_BANNER,
    },
    [CartValidationErrorKey.UPDATE_FAILURE_BANNER]: {
        ...createGenericErrorNotification(),
        id: CartValidationErrorKey.UPDATE_FAILURE_BANNER,
    },
    [CartValidationErrorKey.MIXED_CART_BANNER]: createMixedCartBannerErrorNotification(),
    [CartValidationErrorKey.PICKUP_STORE_CLOSED]: createPickupStoreErrorNotification(),
    [CartValidationErrorKey.MIXED_RX_COLD_ITEM]: createMixedRxWithColdItemErrorNotification(),
    [CartValidationErrorKey.AUTOSHIP_START_DATE_CHANGED]: createAutoshipStartDateChangedBannerNotification(),
    [CartValidationErrorKey.LOYALTY_UNAVAILABLE]: createLoyaltyUnavailableNotification(),
    [CartValidationErrorKey.AUTOSHIP_FUTURE_START_DATE_ERROR]: futureStartDateErrorBannerNotification,
    [CartValidationErrorKey.AUTOSHIP_SCHEDULED_DATE_INVALID_FULFILLMENT_METHOD]: futureStartDateErrorBannerNotification,
    [CartValidationErrorKey.AUTOSHIP_SCHEDULED_DATE_OUT_OF_ALLOWED_RANGE]: futureStartDateErrorBannerNotification,
    [CartValidationErrorKey.INELIGIBLE_ITEM]: createIneligibleItemErrorNotification(),
};

export const checkoutValidationNotificationMap: Partial<
    Record<CheckoutValidationErrorKey | CartValidationErrorKey, Notification>
> = {
    [CheckoutValidationErrorKey.ADD_CARD_FAILURE_BANNER]: {
        ...createUnexpectedErrorNotification(),
        id: CheckoutValidationErrorKey.ADD_CARD_FAILURE_BANNER,
    },
    [CheckoutValidationErrorKey.PAYMENT_FAILURE]: createPaymentUnavailableErrorNotification(),
    [CheckoutValidationErrorKey.MARKET_SHIPPING_ADDRESS_ERROR]: createMarketShippingAddressErrorNotification(),
    [CheckoutValidationErrorKey.PAYMENT_SERVICE_ERROR]: createPaymentUnavailableErrorNotification(),
    [CheckoutValidationErrorKey.MISSING_SHIPPING_DETAILS]: createMissingShippingDetailsBannerErrorNotification(),
    [CheckoutValidationErrorKey.MISSING_CREDIT_CARD_FOR_AUTOSHIP]:
        createMissingCreditCardForAutoshipBannerErrorNotification(),
    [CheckoutValidationErrorKey.SDD_ADDRESS_UNAVAILABLE]: createSddAdrressUnavailableErrorNotification(),
    [CheckoutValidationErrorKey.AMOUNT_MISMATCH]: createAmountMismatchErrorNotification(),
};

// Combined map for convenience
export const validationNotificationMap = {
    ...cartValidationNotificationMap,
    ...checkoutValidationNotificationMap,
};

// Array of all validation error keys
export const CART_VALIDATION_ERROR_KEYS: Array<CartValidationErrorKey | CheckoutValidationErrorKey> = [
    ...Object.values(CartValidationErrorKey),
    ...Object.values(CheckoutValidationErrorKey),
];
